import { changePage } from "#skyward_browse";
import { getBrowseById, getCurrentBrowseName } from "#skyward_browseList";
import { processViewPartHtml, virtualDir } from "#skyward_global";
import { showDialog } from "#skyward_modalLayer";
import { getClosestContentPanel, getCurrentPopupId, notifyDOMLoad, registerLoadDOMCallback } from "#skyward_window";
import { createAjaxManager } from "./skyward.base.ajaxManager";
import { setClickInProgress } from "./skyward.ui.buttonManager";
import { disable, enable, hide, setComboBoxOptions, setValue, show } from "./skyward.ui.controls";
import { addMask } from "./skyward.ui.datamask";
import { getFieldValues } from "./skyward.ui.dataobjectviewer.getFieldValues";
import { getEditableData } from "./skyward.ui.editableBrowse";
import { alignTitles } from "./skyward.ui.fieldset";
import { JQueryInput } from "./types";

interface AjaxData extends Record<string, any>
{
    CRQERecords?: Record<string, any>;
    Remove?: Record<string, string>;
}

const skyward = window.skyward;

const ajaxManager = createAjaxManager(
    {
        requestsStarted: function () { setClickInProgress(true); },
        requestsCompleted: function () { setClickInProgress(false); }
    });

registerLoadDOMCallback(init);

export function getAllInputElements(selector: JQueryInput)
{
    return getFieldValues($(selector));
}

export async function handleResponse(data: AjaxData, prefix?: string, suppressChangeEvent?: boolean, container?: JQuery<HTMLElement>)
{
    await handleResponseInternal(data, prefix, suppressChangeEvent, container);
}

export function registerControlForAjax(selector: JQueryInput)
{
    const element = $(selector);

    const selectorOverride = element.data('ajaxselectoroverride') || "";

    element.on(element.data('ajaxevent'), function (event)
    {
        sendRequest(element, selectorOverride, event);
    });

    if (element.data('ajaxevent') == "ready")
    {
        $(document).ready(function ()
        {
            element.trigger('ready');
        });
    }
}

export function unregisterControlAjax(selector: JQueryInput)
{
    const element = $(selector);
    const ajaxEvent = element.data('ajaxevent');
    if (ajaxEvent)
    {
        element.off(ajaxEvent);
    }
}

async function handleResponseInternal(data: AjaxData, ajaxPrefix?: string, suppressChangeEvent?: boolean, container?: JQuery<HTMLElement>)
{
    if (data === null)
    {
        return;
    }
    const idPrefix = ajaxPrefix ? ajaxPrefix + "_" : "";

    if (!container || !container.length)
    {
        const popupId = getCurrentPopupId();

        if (popupId > -1)
        {
            container = $("#popup" + popupId);
        }
        else
        {
            container = $(document.body);
        }
    }

    let field: string;

    const crqeRecords = data.CRQERecords || {};
    for (field in crqeRecords) 
    {
        const crqeRecord = data.CRQERecords![field];
        //@ts-ignore childrecordquickentry doesn't have a .d.ts file
        skyward.childrecordquickentry.SetCRQERecordValues(crqeRecord, container, idPrefix);
    }

    const removeFields = data.Remove || {};
    for (field in removeFields)
    {
        container.find(removeFields[field]).remove(); // kept for backwards compatibility
        container.find(removeFields[field].replace("#", `#${idPrefix}`)).remove();
    }

    const crqeRemoves = data.CRQERemoves || {};
    for (field in crqeRemoves) 
    {
        const clearButton = container.find('#' + idPrefix + data.CRQERemoves[field] + ' > #' + idPrefix + 'ClearButton > a');
        if (clearButton.length == 1)
        {
            clearButton.trigger('click');
        }
    }

    const updates = data.Update || {};
    for (field in updates)
    {
        if (field.toLowerCase() == "suppresschangeevent")
        {
            await handleResponseInternal({ Update: updates[field] }, ajaxPrefix, true, container);
            continue;
        }

        const nameElement = $(`[name="${field}"]`, container) as JQuery<HTMLInputElement>

        // if field is an object (rather than string)
        if (updates[field] != null && typeof (updates[field]) == 'object')
        {
            // It can be items to default a distinct multi select eel with
            if (nameElement.hasClass("dmsBackingData"))
            {
                updateElement(nameElement);
                continue;
            }

            // Or  an array of updates to a browse's fields
            if (field.toLowerCase() == "browse")
            {
                const browseData: Record<string, unknown> = {};
                browseData.currentPage = 1;

                for (const item in updates[field])
                {
                    const key = item.charAt(0).toLowerCase() + item.slice(1);
                    browseData[key] = updates[field][item];
                }

                changePage(null, getCurrentBrowseName()!, browseData);

                continue;
            }

            // Or each element contained in it is a class that can be used to single out the element to update
            // the element has multiple input elements such as a codeEEL
            const classes = updates[field];
            for (const className in classes)
            {
                if (className.toLowerCase() == "options")
                {
                    setComboBoxOptions(field, updates[field][className], idPrefix);
                }
                else
                {
                    setValue(nameElement.filter("." + className), updates[field][className], suppressChangeEvent);
                }
            }
        }
        // otherwise, update the element whose name is the value of field
        else
        {
            updateElement(nameElement);
        }
    }

    const readOnlys = data.ReadOnly || {};
    for (field in readOnlys)
    {
        let control = $(`[name='${field}']`, container);

        if (control.length < 1)
        {
            control = $(`#${field}`, container);
            if (control.length < 1)
            {
                control = $(`#${idPrefix}${field}`, container);
            }
            if (control.length < 1)
            {
                continue;
            }
        }

        if (readOnlys[field])
        {
            disable(control, idPrefix);
        }
        else
        {
            enable(control, idPrefix);
        }
    }

    const fieldDatas = data.FieldData || {};
    for (field in fieldDatas)
    {
        let control = $(`[name="${field}"]`, container);

        if (control.length < 1)
        {
            control = $(`#${field}`, container);
            if (control.length < 1)
            {
                control = $(`#${idPrefix}${field}`, container);
            }
            if (control.length < 1)
            {
                continue;
            }
        }

        for (const dataKey in fieldDatas[field])
        {
            control.data(dataKey, fieldDatas[field][dataKey]);
        }
    }

    const hiddenFields = data.Hidden || {};
    const animateField = data.AnimateHidden || {};
    for (field in hiddenFields)
    {

        if (hiddenFields[field])
        {
            hide(field, animateField[field] !== false, idPrefix);
        }
        else
        {
            show(field, animateField[field] !== false, idPrefix);
        }
    }

    const message = data.Message;
    if (message)
    {
        showDialog(message.Title || "", message.Text || "", []);
    }

    const appendFields = data.Append || {};
    for (field in appendFields)
    {
        // Object requires a unique id and content and optional class. The ID is used for the wrapper div and the javascript selector
        const appendObject = appendFields[field];
        const foundField = container.find(field).add(container.find(field.replace("#", `#${idPrefix}`)));
        const foundFieldPrefix = foundField.getPrefix();
        let content = $();
        if (appendObject.Content)
        {
            const processedHTML = await processViewPartHtml(appendObject.Content);
            content = $(processedHTML);
        }

        // don't add id prefix to elements with data-id-prefix as they require their original id to work property. example: browses
        for (const contentElem of content)
        {
            const jqContentElem = $(contentElem);
            if (jqContentElem.data('idPrefix') !== undefined)
            {
                continue;
            }
            const contentClosestDataIdPrefix = jqContentElem.closest("[data-id-prefix]");
            jqContentElem.find("[id]").addBack("[id]").each(function ()
            {
                const element = $(this);
                // we want to exclude children that are being ajaxed in with a parent that has a data id prefix.
                const thisClosestDataIdPrefix = element.closest("[data-id-prefix]");

                if (thisClosestDataIdPrefix.length != contentClosestDataIdPrefix.length)
                {
                    return;
                }

                if (thisClosestDataIdPrefix.length > 0 && thisClosestDataIdPrefix[0] != contentClosestDataIdPrefix[0])
                {
                    return;
                }

                // don't add the prefix if its already included 
                if (element.attr("id")?.startsWith(foundFieldPrefix))
                {
                    return;
                }

                this.id = foundFieldPrefix + this.id;
            });
        }

        if (appendObject.Content && appendObject.Class)
        {
            foundField.append($("<div />").html(content as any).attr("id", foundFieldPrefix + appendObject.Id).addClass(appendObject.Class));
        }
        else if (appendObject.Content)
        {
            if (appendObject.NoDiv)
            {
                foundField.append(content);
            }
            else
            {
                foundField.append($("<div />").html(content as any).attr("id", foundFieldPrefix + appendObject.Id));
            }
        }

        if (appendObject.Id)
        {
            notifyDOMLoad(`#${idPrefix}${appendObject.Id}`);
        }
    }

    const resetDatePicker = data.ResetDatePicker || {};
    for (field in resetDatePicker)
    {
        let element = $(`#${field},#${idPrefix}${field}`, container);
        if (element.length == 0)
        {
            element = $(`[name="${field}"]`, container);
        }

        element.val("").trigger("change");
    }

    if (data.Remove || data.Update)
    {
        alignTitles(container);
    }

    function updateElement(nameElement: JQuery<HTMLInputElement>)
    {
        if (nameElement.length == 0)
        {
            //these controls are rendered with the dataObjectViewerId prepended to their name
            nameElement = $(`#${idPrefix}${field}`, container);
        }

        //The following check takes care of tabs so that after the value is updated the text will be reselected
        const selectionTypes = ["text", "search", "url", "tel", "password"];
        const nameElementIsActive =
            nameElement[0] &&
            document.activeElement &&
            nameElement[0] == document.activeElement &&
            selectionTypes.indexOf(nameElement[0].type) != -1 &&
            typeof nameElement[0].selectionStart == "number" &&
            nameElement[0].selectionStart == 0 &&
            nameElement[0].selectionEnd! > 0 &&
            nameElement[0].selectionEnd == nameElement[0].value.length;

        setValue(nameElement, updates[field], suppressChangeEvent);

        // This attached the necessary events so the value can be picked up by the save without giving the input focus
        const elementData = nameElement.data();
        if (nameElement.is("[data-mask]"))
        {
            addMask(nameElement, elementData.mask);
        }

        if (nameElementIsActive)
        {
            nameElement.trigger("select");
        }
    }
}

function sendRequest(selector: JQueryInput, contentSelectorOverride: JQuery<HTMLElement>, event: JQuery.TriggeredEvent)
{
    const element = $(selector);

    if (element.hasClass("ui-disabled") && element.hasClass("button"))
    {
        return;
    }

    if (element.hasClass("ajaxDisableMultipleClicks"))
    {
        element.addClass("ui-disabled");
    }

    const prefix = element.getPrefix()?.slice(0, -1); // remove trailing underscore from data-id-prefix
    const area = element.data("ajaxarea");
    const controller = element.data("ajaxcontroller");
    const action = element.data("ajaxaction");
    let ajaxContext = $(element.data("ajaxcontext") || contentSelectorOverride) as JQuery<HTMLElement>;

    if (!ajaxContext.length)
    {
        ajaxContext = getClosestContentPanel(element);

        if (!ajaxContext.length)
        {
            ajaxContext = $(document.body);
        }
    }

    getAllInputElements(ajaxContext).then(data =>
    {
        data._controlAjaxFiredFrom = event.target.name ? event.target.name : event.target.id;

        const browse = getBrowseById(getCurrentBrowseName()!);
        if (browse && browse.browseName)
        {
            data[browse.browseName] = getEditableData(browse.browseName);
        }

        ajaxManager.ajax(
            {
                url: `${virtualDir}${area}/${controller}/${action}`,
                type: "POST",
                dataType: "JSON",
                data: data,
                success: async function (data)
                {
                    await handleResponseInternal(data, prefix, false, $(ajaxContext));
                    if (element.hasClass("ajaxDisableMultipleClicks"))
                    {
                        element.removeClass("ui-disabled");
                    }
                }
            },
            {
                requestID: element.attr("id")
            });
    });
}

function init(selector: JQueryInput)
{
    $(selector).find("[data-ajaxaction]").each(function (_index, element)
    {
        registerControlForAjax(element);
    });
}